// Биллинг партнёров: общая дата начала, лицевые счета в токенах, настройки,
// отметка API-доступа, пополнения и корректировки, журнал суточных прогонов.
//
// Начисляет evimiz-core раз в сутки по фактам Шанырака; здесь — управление
// счётом. Записи счёта не меняются и не удаляются: ошибку исправляет
// корректировка.

import { useEffect, useMemo, useState } from "react";
import { gql } from "../lib/gql";

type BillingPartner = {
  partnerId: string;
  code: string;
  companyName: string;
  balance: number;
  monthCharged: number;
  dailyCharge: number;
  daysLeft: number | null;
  lastChargeDay: string | null;
  /** Действующая дата начала: своя или общая; null — начислений нет. */
  startDate: string | null;
  /** Своя дата начала; null — общая. */
  startDateCustom: string | null;
  currency: string;
  tokenPrice: number;
  prices: BillingPrice[];
  accountNumber: string;
  accountNumberCustom: string | null;
  legalName: string | null;
  bin: string | null;
  legalAddress: string | null;
  contract: string | null;
  email: string | null;
  issueActs: boolean;
  apiAccess: boolean;
  apiAccessSince: string | null;
};

type BillingPrice = {
  validFrom: string;
  tokenPrice: number;
  createdAt: string;
  createdBy: string | null;
};

type BillingEntry = {
  id: string;
  kind: "CHARGE" | "TOPUP" | "BONUS" | "CORRECTION";
  tokens: number;
  day: string | null;
  chargeKind: string | null;
  archiveDays: number | null;
  amount: number | null;
  currency: string | null;
  paymentRef: string | null;
  paidOn: string | null;
  comment: string | null;
  source: "BILLING" | "SALES" | "PORTAL";
  createdAt: string;
  createdBy: string | null;
};

type BillingRun = {
  day: string;
  ok: boolean;
  finishedAt: string;
  factsDevices: number;
  factsCameras: number;
  charges: number;
  tokens: number;
  skipped: { reason: string; count: number }[];
  error: string | null;
};

type BillingStart = {
  startDate: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

const PARTNER_FIELDS = `partnerId code companyName balance monthCharged dailyCharge daysLeft lastChargeDay
  startDate startDateCustom currency tokenPrice prices { validFrom tokenPrice createdAt createdBy }
  accountNumber accountNumberCustom legalName bin legalAddress contract email issueActs apiAccess apiAccessSince`;

const PARTNERS_QUERY = `query AdminBillingPartners { adminBillingPartners { ${PARTNER_FIELDS} } }`;
const START_QUERY = `query AdminBillingStart { adminBillingStart { startDate updatedAt updatedBy } }`;
const START_MUTATION = `mutation AdminSetBillingStart($startDate: String) {
  adminSetBillingStart(startDate: $startDate) { startDate updatedAt updatedBy }
}`;
const RUNS_QUERY = `query AdminBillingRuns($limit: Int) {
  adminBillingRuns(limit: $limit) {
    day ok finishedAt factsDevices factsCameras charges tokens skipped { reason count } error
  }
}`;
const ENTRIES_QUERY = `query AdminBillingEntries($partnerId: ID!, $limit: Int) {
  adminBillingEntries(partnerId: $partnerId, limit: $limit) {
    id kind tokens day chargeKind archiveDays amount currency paymentRef paidOn comment source createdAt createdBy
  }
}`;
const SETTINGS_MUTATION = `mutation AdminSetBillingSettings($partnerId: ID!, $input: AdminBillingSettingsInput!) {
  adminSetBillingSettings(partnerId: $partnerId, input: $input) { ${PARTNER_FIELDS} }
}`;
const API_MUTATION = `mutation AdminSetBillingApiAccess($partnerId: ID!, $enabled: Boolean!, $validFrom: String!) {
  adminSetBillingApiAccess(partnerId: $partnerId, enabled: $enabled, validFrom: $validFrom) { ${PARTNER_FIELDS} }
}`;
const PRICE_MUTATION = `mutation AdminSetBillingTokenPrice($partnerId: ID!, $tokenPrice: Float!, $validFrom: String!) {
  adminSetBillingTokenPrice(partnerId: $partnerId, tokenPrice: $tokenPrice, validFrom: $validFrom) { ${PARTNER_FIELDS} }
}`;
const TOPUP_MUTATION = `mutation AdminBillingTopUp($partnerId: ID!, $input: AdminBillingTopUpInput!) {
  adminBillingTopUp(partnerId: $partnerId, input: $input) { id }
}`;
const ADJUST_MUTATION = `mutation AdminBillingAdjust($partnerId: ID!, $input: AdminBillingAdjustmentInput!) {
  adminBillingAdjust(partnerId: $partnerId, input: $input) { id }
}`;

const tokensFmt = new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ENTRY_LABELS: Record<BillingEntry["kind"], string> = {
  CHARGE: "Начисление",
  TOPUP: "Пополнение",
  BONUS: "Бонус",
  CORRECTION: "Корректировка",
};

const CHARGE_LABELS: Record<string, string> = {
  DEVICE: "лицензия за домофон",
  LIGHT_DEVICE: "лицензия за Evimiz Light или терминал",
  CAMERA: "видеопоток с архивом",
  RECORDER_CHANNEL: "канал регистратора",
  API_ACCESS: "API-доступ",
};

// Почему факт Шанырака не начислен; «служебные» причины — норма, не ошибка.
const SKIP_LABELS: Record<string, string> = {
  company_unknown: "компания не у партнёра GEO",
  company_ambiguous: "компания у нескольких партнёров",
  not_started: "биллинг не включён",
  before_start: "до даты старта",
  device_unknown: "устройство не найдено",
  not_placed: "не на точке",
  camera_device_unknown: "камера без устройства",
  camera_included: "камера терминала — в лицензии",
  archive_unpriced: "глубина архива без цены",
  recorder_by_channels: "регистратор — по каналам",
  rtsp_by_camera: "RTSP — по камере",
  batyr_by_camera: "Batyr.AI — по камере",
  owner_ambiguous: "у двух компаний за сутки",
};

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDay(day: string | null): string {
  return day ? new Date(`${day}T00:00:00`).toLocaleDateString("ru-RU") : "—";
}

// Цена токена на сутки по истории (по возрастанию даты); истории нет — 1.
function priceOn(prices: BillingPrice[], day: string): number {
  let price = 1;
  for (const p of prices) {
    if (p.validFrom > day) break;
    price = p.tokenPrice;
  }
  return price;
}

const priceFmt = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 4 });

export function BillingPage() {
  const [partners, setPartners] = useState<BillingPartner[]>([]);
  const [runs, setRuns] = useState<BillingRun[]>([]);
  const [start, setStart] = useState<BillingStart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const reload = async () => {
    setError(null);
    try {
      const [p, r, s] = await Promise.all([
        gql<{ adminBillingPartners: BillingPartner[] }>(PARTNERS_QUERY),
        gql<{ adminBillingRuns: BillingRun[] }>(RUNS_QUERY, { limit: 30 }),
        gql<{ adminBillingStart: BillingStart }>(START_QUERY),
      ]);
      setPartners(p.adminBillingPartners);
      setRuns(r.adminBillingRuns);
      setStart(s.adminBillingStart);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const opened = useMemo(() => partners.find((p) => p.partnerId === openId) ?? null, [partners, openId]);

  return (
    <>
      <h2>Биллинг</h2>
      <p className="muted">
        Счета партнёров в токенах. Начисления — раз в сутки по фактам Шанырака: лицензии устройств на точках и
        калитках, видеопоток с архивом камер (по глубине архива) и каналы регистраторов. Записи счёта не меняются:
        ошибку исправляет корректировка.
      </p>
      {error && <div className="error-banner">{error}</div>}

      {start && <BillingStartCard start={start} onChanged={(s) => { setStart(s); reload(); }} />}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Партнёр</th>
              <th style={{ textAlign: "right" }}>Остаток</th>
              <th style={{ textAlign: "right" }}>За месяц</th>
              <th style={{ textAlign: "right" }}>В сутки</th>
              <th style={{ textAlign: "right" }}>Запас, сут.</th>
              <th>Старт</th>
              <th>API</th>
              <th>Лицевой счёт · БИН</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.partnerId}>
                <td>
                  {p.companyName || "—"}
                  <div className="muted">{p.code}</div>
                </td>
                <td style={{ textAlign: "right", color: p.balance < 0 ? "var(--danger)" : undefined }}>
                  {tokensFmt.format(p.balance)}
                </td>
                <td style={{ textAlign: "right" }}>{tokensFmt.format(p.monthCharged)}</td>
                <td style={{ textAlign: "right" }}>{tokensFmt.format(p.dailyCharge)}</td>
                <td style={{ textAlign: "right" }}>{p.daysLeft ?? "—"}</td>
                <td>
                  {formatDay(p.startDate)}
                  {p.startDateCustom && <div className="muted">своя</div>}
                </td>
                <td>{p.apiAccess ? `с ${formatDay(p.apiAccessSince)}` : "—"}</td>
                <td>
                  {p.accountNumber}
                  <div className="muted">{p.bin || "БИН не указан"}</div>
                </td>
                <td>
                  <button className="secondary" style={{ fontSize: 12, padding: "4px 8px" }} onClick={() => setOpenId(p.partnerId)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 24 }}>Суточные прогоны</h3>
      <div className="card">
        {runs.length === 0 ? (
          <div className="muted">Прогонов ещё не было: начисления начнутся с общей даты начала или своей даты партнёра.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Сутки</th>
                <th>Итог</th>
                <th style={{ textAlign: "right" }}>Факты: устройства · камеры</th>
                <th style={{ textAlign: "right" }}>Начислений</th>
                <th style={{ textAlign: "right" }}>Токенов</th>
                <th>Не начислено</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.day}>
                  <td>{formatDay(r.day)}</td>
                  <td style={{ color: r.ok ? "var(--success)" : "var(--danger)" }}>{r.ok ? "прошёл" : r.error || "ошибка"}</td>
                  <td style={{ textAlign: "right" }}>
                    {r.factsDevices} · {r.factsCameras}
                  </td>
                  <td style={{ textAlign: "right" }}>{r.charges}</td>
                  <td style={{ textAlign: "right" }}>{tokensFmt.format(r.tokens)}</td>
                  <td className="muted">
                    {r.skipped.map((s) => `${SKIP_LABELS[s.reason] ?? s.reason}: ${s.count}`).join("; ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {opened && (
        <PartnerBillingModal
          partner={opened}
          onClose={() => setOpenId(null)}
          onChanged={(p) => setPartners((prev) => prev.map((x) => (x.partnerId === p.partnerId ? p : x)))}
          onReload={reload}
        />
      )}
    </>
  );
}

// Общая дата начала биллинга: с неё начисления у всех партнёров без своей
// даты. Пусто — биллинг не включён. Ставится только на сегодня или позже.
function BillingStartCard({ start, onChanged }: { start: BillingStart; onChanged: (s: BillingStart) => void }) {
  const [date, setDate] = useState(start.startDate ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayISO();

  const save = async (value: string | null) => {
    if (value === null && !window.confirm("Снять общую дату начала? Начисления у партнёров без своей даты остановятся.")) return;
    if (value !== null && !window.confirm(`Включить биллинг с ${formatDay(value)} для всех партнёров без своей даты?`)) return;
    setBusy(true);
    setError(null);
    try {
      const data = await gql<{ adminSetBillingStart: BillingStart }>(START_MUTATION, { startDate: value });
      setDate(data.adminSetBillingStart.startDate ?? "");
      onChanged(data.adminSetBillingStart);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Начало биллинга</h3>
      <p className="muted">
        {start.startDate
          ? `Начисления у всех партнёров без своей даты — с ${formatDay(start.startDate)}.`
          : "Биллинг не включён: у партнёров без своей даты начислений нет."}
        {start.updatedBy && ` Менял ${start.updatedBy}, ${new Date(start.updatedAt).toLocaleString("ru-RU")}.`}
      </p>
      {error && <div className="error-banner">{error}</div>}
      <div className="row">
        <label>
          Общая дата начала
          <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button onClick={() => save(date)} disabled={busy || !date || date === start.startDate}>
          {busy ? "…" : "Сохранить"}
        </button>
        {start.startDate && (
          <button className="secondary" onClick={() => save(null)} disabled={busy}>
            Снять
          </button>
        )}
      </div>
    </div>
  );
}

function PartnerBillingModal({
  partner,
  onClose,
  onChanged,
  onReload,
}: {
  partner: BillingPartner;
  onClose: () => void;
  onChanged: (p: BillingPartner) => void;
  onReload: () => void;
}) {
  const [entries, setEntries] = useState<BillingEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(partner.startDateCustom ?? "");
  const [currency, setCurrency] = useState(partner.currency);
  const [accountNumber, setAccountNumber] = useState(partner.accountNumberCustom ?? "");
  const [legalName, setLegalName] = useState(partner.legalName ?? "");
  const [bin, setBin] = useState(partner.bin ?? "");
  const [legalAddress, setLegalAddress] = useState(partner.legalAddress ?? "");
  const [contract, setContract] = useState(partner.contract ?? "");
  const [email, setEmail] = useState(partner.email ?? "");
  const [issueActs, setIssueActs] = useState(partner.issueActs);

  const [newPrice, setNewPrice] = useState("");
  const [priceFrom, setPriceFrom] = useState(todayISO());

  const [apiFrom, setApiFrom] = useState(todayISO());

  const [amount, setAmount] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [paidOn, setPaidOn] = useState(todayISO());
  const [topUpComment, setTopUpComment] = useState("");

  const [adjustKind, setAdjustKind] = useState<"BONUS" | "CORRECTION">("BONUS");
  const [adjustTokens, setAdjustTokens] = useState("");
  const [adjustComment, setAdjustComment] = useState("");

  const loadEntries = async () => {
    try {
      const data = await gql<{ adminBillingEntries: BillingEntry[] }>(ENTRIES_QUERY, { partnerId: partner.partnerId, limit: 200 });
      setEntries(data.adminBillingEntries);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.partnerId]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const saveSettings = () =>
    run("settings", async () => {
      const data = await gql<{ adminSetBillingSettings: BillingPartner }>(SETTINGS_MUTATION, {
        partnerId: partner.partnerId,
        input: { startDate: startDate || null, currency, accountNumber, legalName, bin, legalAddress, contract, email, issueActs },
      });
      onChanged(data.adminSetBillingSettings);
      setAccountNumber(data.adminSetBillingSettings.accountNumberCustom ?? "");
      setEmail(data.adminSetBillingSettings.email ?? "");
      setBin(data.adminSetBillingSettings.bin ?? "");
    });

  const savePrice = () =>
    run("price", async () => {
      const data = await gql<{ adminSetBillingTokenPrice: BillingPartner }>(PRICE_MUTATION, {
        partnerId: partner.partnerId,
        tokenPrice: Number(newPrice.replace(",", ".")),
        validFrom: priceFrom,
      });
      onChanged(data.adminSetBillingTokenPrice);
      setNewPrice("");
    });

  const setApi = (enabled: boolean) =>
    run("api", async () => {
      const data = await gql<{ adminSetBillingApiAccess: BillingPartner }>(API_MUTATION, {
        partnerId: partner.partnerId,
        enabled,
        validFrom: apiFrom,
      });
      onChanged(data.adminSetBillingApiAccess);
    });

  const topUp = () =>
    run("topup", async () => {
      await gql(TOPUP_MUTATION, {
        partnerId: partner.partnerId,
        input: { amount: Number(amount.replace(",", ".")), paymentRef, paidOn, comment: topUpComment || null },
      });
      setAmount("");
      setPaymentRef("");
      setPaidOn(todayISO());
      setTopUpComment("");
      await loadEntries();
      onReload();
    });

  const adjust = () =>
    run("adjust", async () => {
      await gql(ADJUST_MUTATION, {
        partnerId: partner.partnerId,
        input: { kind: adjustKind, tokens: Number(adjustTokens.replace(",", ".")), comment: adjustComment },
      });
      setAdjustTokens("");
      setAdjustComment("");
      await loadEntries();
      onReload();
    });

  const amountNum = Number(amount.replace(",", "."));
  const paidOnPrice = priceOn(partner.prices, paidOn);
  const tokensPreview = amountNum > 0 ? amountNum / paidOnPrice : null;
  const today = todayISO();
  const scheduled = partner.prices.filter((p) => p.validFrom > today);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "grid", placeItems: "center", zIndex: 100, overflowY: "auto", padding: 16 }}
    >
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>
            {partner.companyName || partner.code} · остаток {tokensFmt.format(partner.balance)}
          </h3>
          <button className="secondary" onClick={onClose}>
            Закрыть
          </button>
        </div>
        {error && <div className="error-banner">{error}</div>}

        <h4>Настройки и реквизиты</h4>
        <div className="form-row">
          <label>
            Своя дата начала
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            Валюта
            <input type="text" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
          </label>
          <label>
            Номер лицевого счёта
            <input
              type="text"
              inputMode="numeric"
              value={accountNumber}
              placeholder={partner.code}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            Наименование (юр.)
            <input type="text" value={legalName} placeholder={partner.companyName} onChange={(e) => setLegalName(e.target.value)} />
          </label>
          <label>
            БИН / ИИН
            <input type="text" value={bin} onChange={(e) => setBin(e.target.value)} />
          </label>
        </div>
        <div className="form-row">
          <label>
            Адрес
            <input type="text" value={legalAddress} onChange={(e) => setLegalAddress(e.target.value)} />
          </label>
          <label>
            Договор
            <input type="text" value={contract} placeholder="Договор оферты" onChange={(e) => setContract(e.target.value)} />
          </label>
          <label>
            E-mail для документов
            <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={issueActs} onChange={(e) => setIssueActs(e.target.checked)} />
          Выставлять АВР пакетом
        </label>
        <button onClick={saveSettings} disabled={busy !== null}>
          {busy === "settings" ? "…" : "Сохранить"}
        </button>
        <p className="muted">
          Своя дата начала пуста — действует общая. Ставится только на сегодня или позже: прошедшие сутки уже
          прогнаны. Валюта меняется, пока у
          партнёра нет записей счёта. Номер лицевого счёта пуст — в портале бухгалтерии партнёр под кодом {partner.code}; у
          компаний, общих со Спутником, ставьте номер Спутника. Реквизиты идут в АВР, счета и акты сверки портала.
        </p>

        <h4>Цена токена</h4>
        <p className="muted">
          Сегодня — {priceFmt.format(partner.tokenPrice)} {partner.currency} за токен.
          {scheduled.length > 0 &&
            ` Запланировано: ${scheduled.map((p) => `с ${formatDay(p.validFrom)} — ${priceFmt.format(p.tokenPrice)}`).join("; ")}.`}
        </p>
        <div className="row">
          <label>
            Новая цена, {partner.currency}
            <input type="text" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
          </label>
          <label>
            С суток
            <input type="date" value={priceFrom} min={today} onChange={(e) => setPriceFrom(e.target.value)} />
          </label>
          <button onClick={savePrice} disabled={busy !== null || !(Number(newPrice.replace(",", ".")) > 0)}>
            {busy === "price" ? "…" : "Сохранить цену"}
          </button>
        </div>
        <p className="muted">
          Деньги начислений считаются по цене их суток, поэтому новая цена действует с выбранных суток и не меняет уже
          начисленное. Пополнение переводится в токены по цене на дату оплаты.
        </p>
        {partner.prices.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>С суток</th>
                <th style={{ textAlign: "right" }}>Цена</th>
                <th>Кто</th>
              </tr>
            </thead>
            <tbody>
              {partner.prices.map((p) => (
                <tr key={p.validFrom}>
                  <td>{formatDay(p.validFrom)}</td>
                  <td style={{ textAlign: "right" }}>{priceFmt.format(p.tokenPrice)}</td>
                  <td className="muted">{p.createdBy || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h4>API-доступ</h4>
        <p className="muted">
          {partner.apiAccess
            ? `Пользуется API с ${formatDay(partner.apiAccessSince)}: каждое устройство с подключением — ещё 1000 токенов в месяц.`
            : "Не отмечен."}
        </p>
        <div className="row">
          <label>
            С суток
            <input type="date" value={apiFrom} onChange={(e) => setApiFrom(e.target.value)} />
          </label>
          <button onClick={() => setApi(true)} disabled={busy !== null}>
            Включить
          </button>
          <button className="secondary" onClick={() => setApi(false)} disabled={busy !== null}>
            Выключить
          </button>
        </div>

        <h4>Пополнение</h4>
        <div className="form-row">
          <label>
            Сумма, {partner.currency}
            <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label>
            № платёжного документа
            <input type="text" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} />
          </label>
          <label>
            Дата оплаты по банку
            <input type="date" value={paidOn} max={today} onChange={(e) => setPaidOn(e.target.value)} />
          </label>
          <label>
            Комментарий
            <input type="text" value={topUpComment} onChange={(e) => setTopUpComment(e.target.value)} />
          </label>
        </div>
        <button onClick={topUp} disabled={busy !== null || !(amountNum > 0) || !paymentRef.trim() || !paidOn}>
          {busy === "topup" ? "…" : "Внести оплату"}
        </button>
        {tokensPreview !== null && (
          <span className="muted">
            {" "}
            — будет зачислено {tokensFmt.format(tokensPreview)} токенов по цене {priceFmt.format(paidOnPrice)} {partner.currency}
          </span>
        )}

        <h4>Бонус или корректировка</h4>
        <div className="form-row">
          <label>
            Вид
            <select value={adjustKind} onChange={(e) => setAdjustKind(e.target.value as "BONUS" | "CORRECTION")}>
              <option value="BONUS">Бонус (+)</option>
              <option value="CORRECTION">Корректировка (±)</option>
            </select>
          </label>
          <label>
            Токенов
            <input type="text" value={adjustTokens} onChange={(e) => setAdjustTokens(e.target.value)} />
          </label>
          <label>
            Причина
            <input type="text" value={adjustComment} onChange={(e) => setAdjustComment(e.target.value)} />
          </label>
        </div>
        <button onClick={adjust} disabled={busy !== null || !adjustTokens.trim() || !adjustComment.trim()}>
          {busy === "adjust" ? "…" : "Внести"}
        </button>

        <h4>Записи счёта</h4>
        {entries.length === 0 ? (
          <div className="muted">Записей нет.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Вид</th>
                <th style={{ textAlign: "right" }}>Токенов</th>
                <th>Подробности</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    {e.day ? formatDay(e.day) : e.paidOn ? `оплата ${formatDay(e.paidOn)}` : new Date(e.createdAt).toLocaleString("ru-RU")}
                    {e.paidOn && <div className="muted">внесено {new Date(e.createdAt).toLocaleString("ru-RU")}</div>}
                  </td>
                  <td>
                    {ENTRY_LABELS[e.kind]}
                    {e.chargeKind && (
                      <span className="muted">
                        {" "}· {CHARGE_LABELS[e.chargeKind] ?? e.chargeKind}
                        {e.archiveDays != null && `, ${e.archiveDays} сут`}
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "right", color: e.tokens < 0 ? "var(--danger)" : "var(--success)" }}>
                    {e.tokens > 0 ? "+" : ""}
                    {tokensFmt.format(e.tokens)}
                  </td>
                  <td className="muted">
                    {[
                      e.amount != null && e.currency ? `${tokensFmt.format(e.amount)} ${e.currency}` : null,
                      e.paymentRef ? `док. ${e.paymentRef}` : null,
                      e.comment,
                      e.source === "PORTAL" ? "портал бухгалтерии" : e.createdBy,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
